import React, {useRef, useState} from 'react';
import {
  Keyboard,
  Platform,
  ScrollView,
  TouchableOpacity,
  View,
  useWindowDimensions,
  StyleSheet,
} from 'react-native';
import Text from '../../../components/Text';
import * as yup from 'yup';
import Button from '../../../components/Button';
import Heading from '../../../components/Heading';
import HStack from '../../../components/HStack';
import {Feather} from '@expo/vector-icons';
import AsterixDialog from '../../../components/AsterixDialog';
import RegisterActionBar from '../../../components/RegisterActionBar';
import RegisterPrevButton from '../../../components/RegisterPrevButton';
import {useDispatch, useSelector} from 'react-redux';
import {RegistrationData} from '../types';
import {RootStore} from '../../../redux/store';
import {residentApi} from '../../../api/resident';
import {showErrorMessage} from '../../../service/flashMessage';
import {setRegisterData} from '../registerReducer';
import ScreenWrapper from '../components/ScreenWrapper';
import { SafeAreaView } from 'react-native-safe-area-context';
import isWeb from '../../../utils/isWeb';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import TextInput from '../../../components/TextInput';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {E_NAVIGATION_DESTINATIONS, E_STORAGE_KEYS} from '../../../@types';
import {getUniqueId} from 'react-native-device-info';
import {
  checkUnitPeopleCode,
  linkPersonToUnitLink,
} from '../../People/peopleReducer';
import getErrorMessage from '../../../utils/getErrorMessage';

const passwordValidationSchema = yup.object().shape({
  password: yup.string().required('Password is required'),
  confirmPassword: yup
    .string()
    .test('passwords-match', 'Passwords must match', function (value) {
      return this.parent.password === value;
    }),
});

const Password = ({navigation}) => {
  const [loading, setLoading] = useState(false);
  const [passwordHidden, setpasswordHidden] = useState(true);
  const dispatch = useDispatch();
  const registerValues = useSelector((state: RootStore) => state?.register);
  const scrollViewRef = useRef<ScrollView>(null);

  const {control, handleSubmit} = useForm({
    defaultValues: {
      password: registerValues.password,
      confirmPassword: registerValues.password,
    },
    resolver: yupResolver(passwordValidationSchema),
  });

  const { width } = useWindowDimensions();

  const registerUser = async (password: string) => {
    const deviceId = await getUniqueId();

    const allRegisterValues = {
      ...registerValues,
      password,
      listValidDeviceIds: [deviceId],
    };

    setLoading(true);
    try {
      const res = await residentApi().post(
        '/people/register',
        allRegisterValues,
      );
      const result = res.data;
      if (result.errors) {
        showErrorMessage('Password: ' + result?.errors[0]);
      } else {
        const data = result.data;
        if (data?.token) {
          const {token, refreshToken} = data;
          await AsyncStorage.setItem(
            E_STORAGE_KEYS.AUTH_TOKEN,
            JSON.stringify({
              token,
              refreshToken,
            }),
          );
        }

        if (!!registerValues.linkCode) {
          await linkPersonToUnit(data?.id);
          return;
        } else {
          navigation.navigate(
            E_NAVIGATION_DESTINATIONS.CONFIRIRMATION_CODES_ENTRY,
            {
              personId: data?.id,
              errors: data?.errors,
            },
          );
        }
      }
    } catch (error) {
      showErrorMessage('Password - 2: ' + getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const linkPersonToUnit = async personId => {
    try {
      const unitPeoplelink = await checkUnitPeopleCode(registerValues.linkCode);
      const result = await linkPersonToUnitLink({
        unitPeopleLinkId: unitPeoplelink?.id,
      });

      if (result.errors) {
        showErrorMessage('Password - 3: ' + result?.errors[0]);
      } else {
        navigation.navigate(
          E_NAVIGATION_DESTINATIONS.CONFIRIRMATION_CODES_ENTRY,
          {personId},
        );
      }
    } catch (error) {
      showErrorMessage('Password - 4: ' + error.message);
      navigation.navigate(
        E_NAVIGATION_DESTINATIONS.CONFIRIRMATION_CODES_ENTRY,
        {personId},
      );
    }
  };

  const handlePasswordSubmit = async (values: {
    password: string;
  }) => {
    Keyboard.dismiss();

    const registrationData: RegistrationData = {
      password: values.password,
    };
    //@ts-ignore
    dispatch(setRegisterData(registrationData));

    await registerUser(values.password);
  };

  const onError = () => {
    Keyboard.dismiss();
  }

  return (
    <ScreenWrapper alignTop>
      <SafeAreaView
        style={[
          styles.safeAreaView,
          isWeb && {backgroundColor: '#ffffff', width: width / 3},
        ]}>
        <RegisterActionBar step={isWeb ? 8 : 9} totalSteps={isWeb ? 9 : 10} />
        <ScrollView
          style={styles.scrollView}
          keyboardShouldPersistTaps="handled"
          ref={scrollViewRef}
          showsVerticalScrollIndicator
          contentContainerStyle={styles.scrollContent}>
          <View style={styles.container}>
            <Heading size="lg">Password</Heading>
            <Text style={styles.text}>
              Type a strong and memorable password to secure your account.
            </Text>
            <TextInput
              //@ts-ignore
              control={control}
              name="password"
              placeholder=""
              label="Password *"
              autoCapitalize="none"
              secureTextEntry={passwordHidden}
              blurOnSubmit={false}
              textContentType="oneTimeCode"
              onFocus={() => {
                setTimeout(() => {
                  scrollViewRef.current?.scrollTo({x: 0, y: 100, animated: true});
                }, 100);
              }}
              InputRightElement={
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() => setpasswordHidden(!passwordHidden)}>
                  <Feather
                    name={passwordHidden ? 'eye-off' : 'eye'}
                    size={24}
                    color="gray"
                  />
                </TouchableOpacity>
              }
              labelStyle={{fontSize: 18}}
            />
            <View style={[styles.passwordRequirements, {marginTop: 20}]}>
              <Text style={styles.requirementText}>
                * Passwords must have at least one non alphanumeric character.
              </Text>
              <Text style={styles.requirementText}>
                * Passwords must have at least one digit.
              </Text>
              <Text style={styles.requirementText}>
                * Passwords must have at least one uppercase.
              </Text>
            </View>
            <TextInput
              //@ts-ignore
              control={control}
              name="confirmPassword"
              placeholder=""
              label="Confirm password *"
              autoCapitalize="none"
              secureTextEntry={passwordHidden}
              blurOnSubmit={false}
              textContentType="oneTimeCode"
              onFocus={() => {
                setTimeout(() => {
                  scrollViewRef.current?.scrollToEnd({animated: true});
                }, 100);
              }}
              InputRightElement={
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() => setpasswordHidden(!passwordHidden)}>
                  <Feather
                    name={passwordHidden ? 'eye-off' : 'eye'}
                    size={24}
                    color="gray"
                  />
                </TouchableOpacity>
              }
              labelStyle={{fontSize: 18}}
            />
          </View>
        </ScrollView>
        <HStack style={styles.hStack}>
          <RegisterPrevButton />
          <Button
            onPress={handleSubmit(handlePasswordSubmit, onError)}
            isLoading={loading}
            isLoadingText={'Submitting ...'}
            style={styles.button}>
            {'Next'}
          </Button>
        </HStack>
      </SafeAreaView>
      <AsterixDialog
        message={"We're saving your information."}
        isSwitching={loading}
      />
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  safeAreaView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    marginHorizontal: 8,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 20,
  },
  container: {
    marginHorizontal: 24,
    marginTop: 16,
  },
  text: {
    marginTop: 32,
    fontSize: 18,
  },
  iconButton: {
    paddingHorizontal: 8,
  },
  passwordRequirements: {
    marginTop: Platform.OS === 'web' ? 0 : -10,
  },
  requirementText: {
    fontSize: 14,
  },
  hStack: {
    marginHorizontal: 24,
    marginTop: 'auto',
    marginBottom: 16,
  },
  button: {
    marginHorizontal: 8,
    flex: 1,
  },
});

export default Password;
